import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  FileText, Plus, X, Save, Loader2, Trash2, FileDown, Search, RotateCcw,
  CheckCircle, Edit3, Send, Mail,
} from "lucide-react";
import api, { formatApiError, API } from "../api";
import { formatEUR as fmtEur } from "../utils/format";
import {
  Button, Card, Input, Select, Label, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const STATUS_TONE = { draft: "default", sent: "warn", paid: "ok", cancelled: "err" };

function computeTotals(lines) {
  let net = 0, vat = 0;
  const byRate = {};
  (lines || []).forEach((ln) => {
    const lineNet = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
    const lineVat = lineNet * Number(ln.vat_rate || 0) / 100;
    net += lineNet; vat += lineVat;
    const r = Number(ln.vat_rate || 0);
    byRate[r] = byRate[r] || { rate: r, net: 0, vat: 0 };
    byRate[r].net += lineNet;
    byRate[r].vat += lineVat;
  });
  return { net, vat, gross: net + vat, byRate: Object.values(byRate) };
}

// ============================================================
//  Invoice Editor
// ============================================================
function InvoiceEditor({ open, initial, meta, onClose, onSaved }) {
  const { t } = useTranslation();
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setData(initial); }, [initial]);
  if (!open || !data) return null;

  const upd = (patch) => setData((d) => ({ ...d, ...patch }));
  const updCust = (patch) => upd({ customer: { ...(data.customer || {}), ...patch } });
  const updLine = (idx, patch) => {
    const next = [...(data.lines || [])];
    next[idx] = { ...next[idx], ...patch };
    upd({ lines: next });
  };
  const addLine = () => upd({ lines: [...(data.lines || []), { description: "", quantity: 1, unit_price: 0, vat_rate: 20, unit: "Pers." }] });
  const removeLine = (idx) => upd({ lines: data.lines.filter((_, i) => i !== idx) });

  const totals = computeTotals(data.lines);

  const save = async () => {
    if (!data.customer?.name?.trim()) { toast.error(t("invoices.customer_required")); return; }
    if (!data.lines?.length) { toast.error(t("invoices.lines_required")); return; }
    setSaving(true);
    try {
      const payload = {
        customer: data.customer,
        lines: data.lines.map((l) => ({
          description: l.description || "",
          quantity: Number(l.quantity || 0),
          unit_price: Number(l.unit_price || 0),
          vat_rate: Number(l.vat_rate || 0),
          unit: l.unit || "",
        })),
        notes: data.notes || "",
        payment_terms_days: Number(data.payment_terms_days || 14),
        date: data.date || undefined,
        due_date: data.due_date || undefined,
      };
      const res = data.id
        ? await api.patch(`/invoices/${data.id}`, payload)
        : await api.post("/invoices", payload);
      toast.success(t("invoices.saved"));
      onSaved(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-bg-void border-l border-white/10 w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invoice-editor">
        <div className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted">{data.id ? data.invoice_number : t("invoices.new")}</div>
            <div className="text-lg font-semibold text-ink-primary">{data.customer?.name || t("invoices.new_invoice")}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /> {t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="invoice-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("common.save")}
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer */}
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-medium mb-3">{t("invoices.customer")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>{t("invoices.cust_name")}</Label>
                <Input value={data.customer?.name || ""} onChange={(e) => updCust({ name: e.target.value })} placeholder="Hotel Mustermann GmbH" data-testid="inv-cust-name" />
              </div>
              <div>
                <Label>{t("invoices.cust_vat")}</Label>
                <Input value={data.customer?.vat_number || ""} onChange={(e) => updCust({ vat_number: e.target.value })} placeholder="ATU12345678" />
              </div>
              <div className="md:col-span-2">
                <Label>{t("invoices.cust_address")}</Label>
                <textarea value={data.customer?.address || ""} onChange={(e) => updCust({ address: e.target.value })} rows={2} className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand" placeholder="Musterstr. 1&#10;1010 Wien" />
              </div>
              <div>
                <Label>{t("invoices.cust_email")}</Label>
                <Input type="email" value={data.customer?.email || ""} onChange={(e) => updCust({ email: e.target.value })} />
              </div>
              <div>
                <Label>{t("invoices.payment_terms")}</Label>
                <Input type="number" min="0" max="90" value={data.payment_terms_days ?? 14} onChange={(e) => upd({ payment_terms_days: Number(e.target.value) })} />
              </div>
            </div>
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-medium">{t("invoices.lines")}</div>
              <Button variant="secondary" size="sm" onClick={addLine} data-testid="inv-add-line"><Plus size={12} /> {t("invoices.add_line")}</Button>
            </div>
            <div className="space-y-2">
              {(data.lines || []).map((ln, idx) => (
                <Card key={idx} className="p-3 space-y-2" data-testid={`inv-line-${idx}`}>
                  <Input value={ln.description} onChange={(e) => updLine(idx, { description: e.target.value })} placeholder={t("invoices.line_desc_ph")} data-testid={`inv-line-desc-${idx}`} />
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <Input type="number" step="0.01" value={ln.quantity} onChange={(e) => updLine(idx, { quantity: e.target.value })} placeholder={t("invoices.qty")} data-testid={`inv-line-qty-${idx}`} />
                    <Input value={ln.unit || ""} onChange={(e) => updLine(idx, { unit: e.target.value })} placeholder="Pers." />
                    <Input type="number" step="0.01" value={ln.unit_price} onChange={(e) => updLine(idx, { unit_price: e.target.value })} placeholder={t("invoices.unit_price")} data-testid={`inv-line-price-${idx}`} />
                    <Select value={ln.vat_rate} onChange={(e) => updLine(idx, { vat_rate: Number(e.target.value) })} data-testid={`inv-line-vat-${idx}`}>
                      {(meta.vat_rates || []).map((r) => <option key={r} value={r}>{r}%</option>)}
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} data-testid={`inv-line-remove-${idx}`}>
                      <Trash2 size={12} className="text-err" />
                    </Button>
                  </div>
                  <div className="text-right text-xs num text-ink-muted">
                    {t("invoices.line_total")}: {fmtEur(Number(ln.quantity || 0) * Number(ln.unit_price || 0))}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Totals */}
          <Card className="p-4 space-y-1 text-sm bg-bg-surface" data-testid="invoice-totals">
            <div className="flex justify-between text-ink-secondary"><span>{t("invoices.net")}</span><span className="num">{fmtEur(totals.net)}</span></div>
            {totals.byRate.map((v) => (
              <div key={v.rate} className="flex justify-between text-ink-muted text-xs">
                <span>{t("invoices.vat")} {v.rate}% ({t("invoices.basis")} {fmtEur(v.net)})</span>
                <span className="num">{fmtEur(v.vat)}</span>
              </div>
            ))}
            <div className="flex justify-between text-base font-semibold text-ink-primary border-t border-white/10 pt-2">
              <span>{t("invoices.gross")}</span><span className="num">{fmtEur(totals.gross)}</span>
            </div>
          </Card>

          <div>
            <Label>{t("invoices.notes")}</Label>
            <textarea value={data.notes || ""} onChange={(e) => upd({ notes: e.target.value })} rows={2} className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm" placeholder={t("invoices.notes_ph")} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Send Email Modal
// ============================================================
function SendEmailModal({ invoice, onClose, onSent }) {
  const { t } = useTranslation();
  const [toEmail, setToEmail] = useState(invoice?.customer?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!invoice) return null;

  const send = async () => {
    const trimmed = (toEmail || "").trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t("invoices.email_invalid"));
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/invoices/${invoice.id}/send`, {
        to_email: trimmed,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      const mode = r.data?.mode || "console";
      toast.success(
        mode === "smtp" ? t("invoices.email_sent", { email: trimmed }) : t("invoices.email_sent_console", { email: trimmed })
      );
      onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-surface border border-white/10 rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="invoice-send-modal">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-brand">{invoice.invoice_number}</div>
            <div className="text-base font-semibold text-ink-primary flex items-center gap-2"><Mail size={16} /> {t("invoices.send_modal_title")}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label>{t("invoices.send_to")}</Label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="kunde@firma.at"
              data-testid="invoice-send-email"
            />
            <p className="text-xs text-ink-muted mt-1">{t("invoices.send_to_hint")}</p>
          </div>
          <div>
            <Label>{t("invoices.send_subject")}</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("invoices.send_subject_ph", { num: invoice.invoice_number })}
              data-testid="invoice-send-subject"
            />
          </div>
          <div>
            <Label>{t("invoices.send_message")}</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand"
              placeholder={t("invoices.send_message_ph")}
              data-testid="invoice-send-message"
            />
          </div>
          <div className="text-xs text-ink-muted bg-bg-void rounded-md px-3 py-2 border border-white/5">
            {t("invoices.send_attached_hint", { file: `${invoice.invoice_number}.pdf` })}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={send} disabled={sending} data-testid="invoice-send-confirm">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {t("invoices.send_now")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Page
// ============================================================
export default function InvoicesPage() {
  const { t } = useTranslation();
  const [meta, setMeta] = useState({ vat_rates: [], statuses: [] });
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ open: 0, paid: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [sendingInv, setSendingInv] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (search) q.set("q", search);
      if (statusFilter) q.set("status", statusFilter);
      const [r, m] = await Promise.all([
        api.get(`/invoices?${q.toString()}`),
        api.get("/invoices/meta"),
      ]);
      setItems(r.data.items || []);
      setTotals(r.data.totals || { open: 0, paid: 0 });
      setMeta(m.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const handleNew = () => setEditing({
    customer: { name: "", address: "", vat_number: "", email: "" },
    lines: [{ description: "", quantity: 1, unit_price: 0, vat_rate: 20, unit: "Pers." }],
    notes: "",
    payment_terms_days: 14,
  });

  const handleEdit = async (id) => {
    try {
      const r = await api.get(`/invoices/${id}`);
      setEditing(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (inv, status) => {
    try {
      await api.post(`/invoices/${inv.id}/status`, { status });
      toast.success(t("invoices.status_updated"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createCredit = async (inv) => {
    if (!window.confirm(t("invoices.credit_confirm", { num: inv.invoice_number }))) return;
    try {
      const r = await api.post(`/invoices/${inv.id}/credit`);
      toast.success(t("invoices.credit_created", { num: r.data.invoice_number }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("studioops_token");
      const res = await fetch(`${API}/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="invoices-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
            <FileText size={12} /> {t("invoices.kicker")}
          </div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("invoices.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1 max-w-2xl">{t("invoices.subtitle")}</p>
        </div>
        <Button onClick={handleNew} data-testid="invoice-new"><Plus size={14} /> {t("invoices.new")}</Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="bg-bg-surface border border-warn/30 rounded-xl px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("invoices.kpi_open")}</div>
          <div className="num text-xl font-medium">{fmtEur(totals.open)}</div>
        </div>
        <div className="bg-bg-surface border border-ok/30 rounded-xl px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("invoices.kpi_paid")}</div>
          <div className="num text-xl font-medium">{fmtEur(totals.paid)}</div>
        </div>
        <div className="bg-bg-surface border border-white/10 rounded-xl px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("invoices.kpi_count")}</div>
          <div className="num text-xl font-medium">{items.length}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 max-w-xs">
          <Label>{t("invoices.search")}</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("invoices.search_ph")} className="pl-9" data-testid="inv-search" />
          </div>
        </div>
        <div>
          <Label>{t("invoices.status_filter")}</Label>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="inv-status-filter">
            <option value="">{t("common.all")}</option>
            {meta.statuses.map((s) => <option key={s} value={s}>{t(`invoices.status_${s}`, s)}</option>)}
          </Select>
        </div>
        {(search || statusFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatusFilter(""); }}>
            <RotateCcw size={12} /> {t("common.reset")}
          </Button>
        )}
      </div>

      {loading ? <div className="flex items-center justify-center py-12"><Spinner size={20} /></div> : items.length === 0 ? (
        <EmptyState icon={FileText} title={t("invoices.empty_title")} description={t("invoices.empty_sub")} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">{t("invoices.col_number")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("invoices.col_customer")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("invoices.col_date")}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t("invoices.col_total")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("invoices.col_status")}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`inv-row-${inv.id}`}>
                    <td className="px-4 py-3">
                      <div className="num text-ink-primary font-medium">{inv.invoice_number}</div>
                      {inv.type === "credit_note" && <Badge tone="err" className="text-[9px] mt-0.5">Storno</Badge>}
                    </td>
                    <td className="px-4 py-3 text-ink-secondary truncate max-w-[200px]">{inv.customer?.name || "—"}</td>
                    <td className="px-4 py-3 text-ink-secondary whitespace-nowrap">{fmtDate(inv.date)}</td>
                    <td className={`px-4 py-3 num text-right font-medium ${inv.totals.gross_total < 0 ? "text-err" : "text-ink-primary"}`}>{fmtEur(inv.totals.gross_total)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[inv.status] || "default"}>{t(`invoices.status_${inv.status}`, inv.status)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {inv.status !== "cancelled" && inv.type !== "credit_note" && (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(inv.id)} data-testid={`inv-edit-${inv.id}`}>
                            <Edit3 size={12} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(inv)} data-testid={`inv-pdf-${inv.id}`}>
                          <FileDown size={12} />
                        </Button>
                        {inv.status === "sent" && (
                          <Button variant="ghost" size="sm" onClick={() => setStatus(inv, "paid")} data-testid={`inv-mark-paid-${inv.id}`}>
                            <CheckCircle size={12} className="text-ok" />
                          </Button>
                        )}
                        {inv.status !== "paid" && inv.status !== "cancelled" && inv.type !== "credit_note" && (
                          <Button variant="ghost" size="sm" onClick={() => setSendingInv(inv)} data-testid={`inv-send-email-${inv.id}`}>
                            <Mail size={12} /> {t("invoices.send_email")}
                          </Button>
                        )}
                        {inv.status !== "cancelled" && inv.type !== "credit_note" && (
                          <Button variant="ghost" size="sm" onClick={() => createCredit(inv)} data-testid={`inv-storno-${inv.id}`}>
                            {t("invoices.storno")}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <InvoiceEditor
        open={editing != null}
        initial={editing}
        meta={meta}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      {sendingInv && (
        <SendEmailModal
          invoice={sendingInv}
          onClose={() => setSendingInv(null)}
          onSent={() => { setSendingInv(null); load(); }}
        />
      )}
    </div>
  );
}
